import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { notificationsAPI, pushAPI } from '@/lib/api';
import { getTimeAgo } from '@/lib/utils';

// Web Push (T8) support detection + key conversion for PushManager.subscribe.
const canUsePush = () => (
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator
  && typeof window !== 'undefined' && 'PushManager' in window
);

const urlB64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

// Register (or reuse) the SW and create a PushManager subscription, then persist
// it server-side. Returns true if a subscription is active. Best-effort — any
// failure leaves the foreground desktop path as the fallback.
const enablePushSubscription = async () => {
  if (!canUsePush()) return false;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    const { public_key: vapid, configured } = await pushAPI.vapidPublicKey();
    if (!configured || !vapid) return false;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapid),
      });
    }
    const json = sub.toJSON();
    await pushAPI.subscribe({ endpoint: sub.endpoint, keys: json.keys }, navigator.userAgent);
    return true;
  } catch {
    return false;
  }
};

const disablePushSubscription = async () => {
  if (!canUsePush()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js')
      || await navigator.serviceWorker.getRegistration();
    const sub = reg && await reg.pushManager.getSubscription();
    if (sub) {
      await pushAPI.unsubscribe(sub.endpoint).catch(() => {});
      await sub.unsubscribe().catch(() => {});
    }
  } catch {
    /* best-effort */
  }
};

const DESKTOP_NOTIFIED_KEY = 'report360.desktopNotifiedNotificationIds';
const DESKTOP_ENABLED_KEY = 'report360.desktopNotificationsEnabled';
const EMPTY_NOTIFICATIONS = [];

const readCurrentUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.id || user?.user_id || user?.sub || 'anonymous';
  } catch {
    return 'anonymous';
  }
};

const scopedStorageKey = (key, scope) => `${key}.${scope || 'anonymous'}`;

const canUseDesktopNotifications = () => (
  typeof window !== 'undefined'
  && typeof Notification !== 'undefined'
);

const readNotifiedIds = (scope) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(scopedStorageKey(DESKTOP_NOTIFIED_KEY, scope)) || '[]'));
  } catch {
    return new Set();
  }
};

const writeNotifiedIds = (ids, scope) => {
  try {
    localStorage.setItem(scopedStorageKey(DESKTOP_NOTIFIED_KEY, scope), JSON.stringify([...ids].slice(-200)));
  } catch {
    // Best-effort only; notification delivery should not break the bell.
  }
};

const readDesktopEnabled = (scope) => {
  try {
    return localStorage.getItem(scopedStorageKey(DESKTOP_ENABLED_KEY, scope)) !== 'false';
  } catch {
    return true;
  }
};

const writeDesktopEnabled = (enabled, scope) => {
  try {
    localStorage.setItem(scopedStorageKey(DESKTOP_ENABLED_KEY, scope), enabled ? 'true' : 'false');
  } catch {
    // Best-effort preference storage.
  }
};

const showDesktopNotification = ({ title, body, tag, onClick }) => {
  if (!canUseDesktopNotifications() || Notification.permission !== 'granted') return null;
  try {
    const notification = new Notification(title, {
      body,
      tag,
      renotify: false,
      requireInteraction: true,
    });
    if (onClick) notification.onclick = onClick;
    return notification;
  } catch {
    return null;
  }
};

/**
 * Header notification bell — Phase 0 of the workflow/TAT engine.
 *
 * Polls the unread count on a 30s interval (cheap endpoint). Opening the
 * popover fetches the recent list. Clicking an item marks it read and
 * deep-links to the document where the action is due. This is the surface the
 * workflow handoffs flow through — no separate sidebar module.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const notificationScope = useMemo(() => readCurrentUserId(), []);
  const [desktopPermission, setDesktopPermission] = useState(
    canUseDesktopNotifications() ? Notification.permission : 'unsupported',
  );
  const [desktopEnabled, setDesktopEnabled] = useState(() => readDesktopEnabled(notificationScope));
  // Seed-suppress the first poll batch ONLY when the user is turning desktop
  // alerts on this mount (was OFF at mount) — so enabling doesn't blast the whole
  // unread backlog at once. If alerts were ALREADY on at mount, do NOT suppress:
  // otherwise every remount (e.g. a route change that remounts the header) would
  // silently mark the current unread as "notified" and the user "never receives"
  // alerts. Duplicate suppression is handled entirely by the persisted
  // `notifiedIds` set, so an already-shown alert never repeats.
  const desktopInitializedRef = useRef(readDesktopEnabled(notificationScope));
  // Whether a background Web Push subscription is active. When true, the SW
  // delivers alerts (even with the tab closed) and the foreground poll MUST NOT
  // also fire a native Notification — otherwise the same alert shows twice.
  const [pushActive, setPushActive] = useState(false);
  const pushActiveRef = useRef(false);
  pushActiveRef.current = pushActive;
  const markReadRef = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Lightweight badge poll — runs always, even when the popover is closed.
  const { data: countData } = useQuery({
    queryKey: ['notifications', notificationScope, 'unread-count'],
    queryFn: () => notificationsAPI.unreadCount(),
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const unread = countData?.unread_count ?? 0;

  // Full list — only fetched while the popover is open.
  const { data: listData, isLoading } = useQuery({
    queryKey: ['notifications', notificationScope, 'list'],
    queryFn: () => notificationsAPI.list({ limit: 20 }),
    enabled: open,
  });
  const items = listData?.items ?? [];

  const { data: desktopListData } = useQuery({
    queryKey: ['notifications', notificationScope, 'desktop-unread-list'],
    queryFn: () => notificationsAPI.list({ only_unread: true, limit: 10 }),
    enabled: desktopEnabled && desktopPermission === 'granted',
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
  const desktopItems = desktopListData?.items || EMPTY_NOTIFICATIONS;

  const markRead = useMutation({
    mutationFn: (ids) => notificationsAPI.markRead(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  markReadRef.current = markRead;

  const handleOpenItem = (n) => {
    if (!n.is_read) markRead.mutate([n.id]);
    setOpen(false);
    if (n.action_url) navigate(n.action_url);
  };

  const requestDesktopPermission = async () => {
    if (!canUseDesktopNotifications()) return 'unsupported';
    const permission = await Notification.requestPermission();
    setDesktopPermission(permission);
    return permission;
  };

  const handleDesktopToggle = async (checked) => {
    if (!checked) {
      setDesktopEnabled(false);
      writeDesktopEnabled(false, notificationScope);
      disablePushSubscription().finally(() => setPushActive(false));
      return;
    }

    const permission = desktopPermission === 'granted'
      ? 'granted'
      : await requestDesktopPermission();

    if (permission === 'granted') {
      setDesktopEnabled(true);
      writeDesktopEnabled(true, notificationScope);
      // Best-effort background Web Push: if it subscribes, the SW owns delivery
      // and the foreground poll is suppressed (see delivery effect) to avoid
      // duplicate alerts. If it fails/unsupported, foreground alerts remain.
      enablePushSubscription().then(setPushActive);
      showDesktopNotification({
        title: 'Desktop alerts enabled',
        body: unread > 0
          ? `You have ${unread} unread notification${unread === 1 ? '' : 's'}.`
          : 'You will receive alerts for new report360 notifications.',
        tag: 'report360-desktop-alerts-enabled',
        onClick: () => window.focus(),
      });
      queryClient.invalidateQueries({ queryKey: ['notifications', notificationScope, 'desktop-unread-list'] });
    } else {
      setDesktopEnabled(false);
      writeDesktopEnabled(false, notificationScope);
    }
  };

  const sendTestDesktopAlert = () => {
    if (!desktopEnabled) return;
    showDesktopNotification({
      title: 'report360 desktop alert',
      body: 'Desktop notifications are working.',
      tag: `report360-desktop-test-${Date.now()}`,
      onClick: () => window.focus(),
    });
  };

  useEffect(() => {
    const syncFocus = () => {
      if (canUseDesktopNotifications()) {
        setDesktopPermission(Notification.permission);
      }
    };
    syncFocus();
    window.addEventListener('focus', syncFocus);
    window.addEventListener('blur', syncFocus);
    document.addEventListener('visibilitychange', syncFocus);
    return () => {
      window.removeEventListener('focus', syncFocus);
      window.removeEventListener('blur', syncFocus);
      document.removeEventListener('visibilitychange', syncFocus);
    };
  }, []);

  useEffect(() => {
    if (open && canUseDesktopNotifications()) {
      setDesktopPermission(Notification.permission);
    }
  }, [open]);

  // On mount, if alerts are on, detect whether a background push subscription
  // already exists (e.g. from a previous session) so the foreground path knows
  // to stay quiet and let the SW deliver.
  useEffect(() => {
    let cancelled = false;
    if (desktopEnabled && canUsePush()) {
      navigator.serviceWorker.getRegistration('/sw.js')
        .then((reg) => (reg ? reg.pushManager.getSubscription() : null))
        .then((sub) => { if (!cancelled) setPushActive(!!sub); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [desktopEnabled]);

  useEffect(() => {
    if (!desktopEnabled || desktopPermission !== 'granted') return;

    const notifiedIds = readNotifiedIds(notificationScope);
    const currentIds = desktopItems.map((n) => n.id);

    // Background Web Push (SW) owns delivery when active — record current ids as
    // seen (so disabling push later doesn't replay old alerts) but do NOT fire a
    // foreground Notification, otherwise the alert would show twice.
    if (pushActiveRef.current) {
      currentIds.forEach((id) => notifiedIds.add(id));
      writeNotifiedIds(notifiedIds, notificationScope);
      desktopInitializedRef.current = true;
      return;
    }

    if (!desktopInitializedRef.current) {
      currentIds.forEach((id) => notifiedIds.add(id));
      writeNotifiedIds(notifiedIds, notificationScope);
      desktopInitializedRef.current = true;
      return;
    }

    desktopItems.forEach((n) => {
      if (notifiedIds.has(n.id)) return;

      const notification = showDesktopNotification({
        title: n.title || 'New notification',
        body: n.body || 'Open report360 to view details.',
        tag: `report360-notification-${n.id}`,
        onClick: () => {
          window.focus();
          if (n.action_url) navigate(n.action_url);
          if (!n.is_read) markReadRef.current?.mutate([n.id]);
          notification?.close();
        },
      });

      if (notification) notifiedIds.add(n.id);
    });

    writeNotifiedIds(notifiedIds, notificationScope);
  }, [desktopEnabled, desktopItems, desktopPermission, navigate, notificationScope]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative hidden md:inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-muted-foreground hover:text-foreground"
          aria-label="Notifications"
        >
          <Bell size={16} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-2.5">
          <span className="text-sm font-semibold text-foreground">Notifications</span>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => markRead.mutate(undefined)}
              disabled={markRead.isPending}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <CheckCheck size={13} /> Mark all read
            </button>
          )}
        </div>

        {desktopPermission !== 'unsupported' && desktopPermission !== 'denied' && (
          <div className="border-b border-border/60 px-4 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">Desktop alerts</span>
              <Switch
                checked={desktopEnabled && desktopPermission === 'granted'}
                onCheckedChange={handleDesktopToggle}
                aria-label="Toggle desktop alerts"
              />
            </div>
            {desktopEnabled && desktopPermission === 'granted' && (
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={sendTestDesktopAlert}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Send test alert
                </button>
              </div>
            )}
          </div>
        )}

        {desktopPermission === 'unsupported' && (
          <div className="border-b border-border/60 px-4 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">Desktop alerts not supported</span>
              <Switch checked={false} disabled aria-label="Desktop alerts not supported" />
            </div>
          </div>
        )}

        {desktopPermission === 'denied' && (
          <div className="border-b border-border/60 px-4 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">Desktop alerts blocked</span>
              <Switch
                checked={false}
                disabled
                aria-label="Desktop alerts blocked in browser settings"
              />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Allow notifications in browser settings to enable.
            </p>
          </div>
        )}

        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up.</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => handleOpenItem(n)}
                className={`flex w-full flex-col items-start gap-0.5 border-b border-border/40 px-4 py-3 text-left transition-colors hover:bg-secondary/60 ${
                  n.is_read ? '' : 'bg-secondary/30'
                }`}
              >
                <div className="flex w-full items-start gap-2">
                  {!n.is_read && (
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
                  )}
                  <span className={`flex-1 text-sm ${n.is_read ? 'text-muted-foreground' : 'font-medium text-foreground'}`}>
                    {n.title}
                  </span>
                </div>
                {n.body && (
                  <span className="line-clamp-2 pl-4 text-xs text-muted-foreground">{n.body}</span>
                )}
                <span className="pl-4 text-[11px] text-muted-foreground">{getTimeAgo(n.created_at)}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
